
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/lib/context/auth-context";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SupportTicket } from "@/lib/types/support-tickets";
import { createTicket } from "@/lib/services/support-ticket-service";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";

const formSchema = z.object({
  title: z.string().min(5, { message: "Title must be at least 5 characters" }),
  description: z.string().min(10, { message: "Description must be at least 10 characters" }),
  ticket_type: z.enum(['feature_request', 'error_report', 'update_request', 'suggestion', 'general']),
  priority: z.enum(['low', 'medium', 'high', 'critical']),
  related_component: z.string().optional(),
  department: z.string().optional()
});

type FormValues = z.infer<typeof formSchema>;

const TicketForm: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      ticket_type: "general",
      priority: "medium",
      related_component: "",
      department: ""
    }
  });
  
  const onSubmit = async (values: FormValues) => {
    if (!profile) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to submit a ticket.",
        variant: "destructive"
      });
      return;
    }
    
    const ticketData: Omit<SupportTicket, 'id' | 'created_at' | 'updated_at'> = {
      title: values.title,
      description: values.description,
      ticket_type: values.ticket_type,
      priority: values.priority,
      department: values.department || null,
      related_component: values.related_component || null,
      created_by: profile.id,
      assigned_to: null,
      status: 'open',
      closed_at: null,
      resolution_notes: null
    };
    
    const result = await createTicket(ticketData);
    
    if (result) {
      navigate("/support/tickets");
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Enter a title for your ticket" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="ticket_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ticket Type</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select ticket type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="feature_request">Feature Request</SelectItem>
                    <SelectItem value="error_report">Error Report</SelectItem>
                    <SelectItem value="update_request">Update Request</SelectItem>
                    <SelectItem value="suggestion">Suggestion</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Priority</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Provide details about your request or issue" 
                  className="min-h-[120px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="related_component"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Related Component (Optional)</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Dashboard, Certificates, etc." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="department"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Department (Optional)</FormLabel>
                <FormControl>
                  <Input placeholder="Your department" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="flex justify-end gap-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate("/support/tickets")}
          >
            Cancel
          </Button>
          <Button type="submit">Submit Ticket</Button>
        </div>
      </form>
    </Form>
  );
};

export default TicketForm;
